import { useState, useEffect, useMemo } from "react";
import { Plus, Minus, Trash2, ShoppingCart, Tag } from "lucide-react";
import api from "@/lib/api";

export default function StepProducts({ items, setItems }) {
  const [products, setProducts] = useState([]);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const addProduct = (product) => {
    const existing = items.find((i) => i.product_id === product.id);
    if (existing) setItems(items.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    else setItems([...items, { product_id: product.id, product_name: product.name, quantity: 1, unit_price: product.price, monthly_price: product.monthly_price || 0, requires_document: product.requires_document, requires_selfie: product.requires_selfie, requires_terms: product.requires_terms }]);
  };

  const updateQty = (id, delta) => {
    setItems(items.map((i) => {
      if (i.product_id !== id) return i;
      const newQty = i.quantity + delta;
      return newQty < 1 ? i : { ...i, quantity: newQty };
    }));
  };

  const removeItem = (id) => setItems(items.filter((i) => i.product_id !== id));
  const total = items.reduce((s, i) => s + i.unit_price * i.quantity, 0);
  const monthlyTotal = items.reduce((s, i) => s + (i.monthly_price || 0) * i.quantity, 0);

  return (
    <div data-testid="step-products">
      <h2 className="crm-heading mb-2">Selecionar Produtos</h2>
      <p className="text-sm mb-8" style={{ color: "var(--crm-text-muted)" }}>Escolha os produtos e serviços para esta venda.</p>

      {/* Product grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        {products.map((p) => {
          const inCart = items.find((i) => i.product_id === p.id);
          return (
            <div key={p.id} className="crm-card overflow-hidden" data-testid={`wizard-product-${p.id}`}
              style={{ borderColor: inCart ? "var(--crm-accent)" : undefined }}>
              {/* Type badge top */}
              <div className="px-5 pt-4 pb-2 flex items-center justify-between">
                <span className={`crm-badge text-[11px] ${p.type === "service" ? "crm-badge-blue" : "crm-badge-purple"}`}>
                  {p.type === "service" ? "Serviço" : "Produto"}
                </span>
                {inCart && (
                  <span className="crm-badge crm-badge-orange text-[11px] font-bold">
                    <ShoppingCart className="w-3 h-3" /> {inCart.quantity} no carrinho
                  </span>
                )}
              </div>
              {/* Name */}
              <div className="px-5 pb-3">
                <p className="text-base font-bold" style={{ color: "var(--crm-text)" }}>{p.name}</p>
              </div>
              {/* Price */}
              <div className="px-5 pb-3">
                <p className="text-2xl font-extrabold tracking-tight" style={{ color: "var(--crm-text)" }}>
                  ¥{p.price.toLocaleString()}
                </p>
                {p.monthly_price > 0 && (
                  <p className="text-sm font-semibold mt-0.5" style={{ color: "var(--crm-accent)" }}>
                    + ¥{p.monthly_price.toLocaleString()}<span className="font-normal text-xs">/mês</span>
                  </p>
                )}
              </div>
              {/* Requirements */}
              <div className="px-5 pb-4 flex flex-wrap gap-1.5">
                {p.requires_document && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold" style={{ background: "rgba(249,115,22,0.08)", color: "var(--crm-accent)", border: "1px solid rgba(249,115,22,0.2)" }}>DOC</span>}
                {p.requires_selfie && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold" style={{ background: "rgba(59,130,246,0.08)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.2)" }}>SELFIE</span>}
                {p.requires_terms && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold" style={{ background: "rgba(139,92,246,0.08)", color: "#8b5cf6", border: "1px solid rgba(139,92,246,0.2)" }}>TERMOS</span>}
              </div>
              {/* CTA */}
              <div className="px-5 pb-5">
                <button onClick={() => addProduct(p)} className="crm-btn-primary w-full text-sm py-3" data-testid={`wizard-add-product-${p.id}`}>
                  <Plus className="w-4 h-4" /> Adicionar
                </button>
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <div className="col-span-full text-center py-16" style={{ color: "var(--crm-text-dim)" }}>Nenhum produto cadastrado</div>
        )}
      </div>

      {/* Cart */}
      {items.length > 0 && (
        <div className="crm-card overflow-hidden" style={{ border: "2px solid var(--crm-accent)" }}>
          {/* Cart header */}
          <div className="px-6 py-4 flex items-center gap-3" style={{ background: "var(--crm-accent-subtle)", borderBottom: "1px solid var(--crm-border)" }}>
            <ShoppingCart className="w-5 h-5" style={{ color: "var(--crm-accent)" }} />
            <h3 className="text-base font-bold" style={{ color: "var(--crm-text)" }}>
              Carrinho — {items.length} {items.length === 1 ? "item" : "itens"}
            </h3>
          </div>

          {/* Items */}
          <div className="px-6">
            {items.map((item, idx) => (
              <div key={item.product_id} className="flex items-center gap-4 py-4"
                style={{ borderBottom: idx < items.length - 1 ? "1px solid var(--crm-border)" : "none" }}
                data-testid={`wizard-cart-item-${item.product_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: "var(--crm-text)" }}>{item.product_name}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--crm-text-muted)" }}>¥{item.unit_price.toLocaleString()} / unidade</p>
                </div>
                {/* Qty controls */}
                <div className="flex items-center gap-1.5">
                  <button onClick={() => updateQty(item.product_id, -1)} className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                    style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)", color: "var(--crm-text)" }}>
                    <Minus className="w-3.5 h-3.5" />
                  </button>
                  <span className="w-10 text-center text-base font-bold" style={{ color: "var(--crm-text)" }}>{item.quantity}</span>
                  <button onClick={() => updateQty(item.product_id, 1)} className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors"
                    style={{ background: "var(--crm-input-bg)", border: "1px solid var(--crm-border)", color: "var(--crm-text)" }}>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-base font-bold w-28 text-right" style={{ color: "var(--crm-text)" }}>¥{(item.unit_price * item.quantity).toLocaleString()}</p>
                <button onClick={() => removeItem(item.product_id)} className="p-2 rounded-lg hover:bg-red-500/10 hover:text-red-400 transition-all" style={{ color: "var(--crm-text-dim)" }}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Totals */}
          <div className="px-6 py-5" style={{ background: "var(--crm-accent-subtle)", borderTop: "2px solid var(--crm-accent)" }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--crm-text-muted)" }}>Total da Venda</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-extrabold tracking-tight" style={{ color: "var(--crm-text)" }}>
                  ¥{total.toLocaleString()}
                </p>
                {monthlyTotal > 0 && (
                  <p className="text-base font-bold mt-1" style={{ color: "var(--crm-accent)" }}>
                    + ¥{monthlyTotal.toLocaleString()}<span className="text-sm font-normal">/mês</span>
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
